'use client';

import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/forms/button';
import { UserAuthForm } from '@/components/auth/SignInForm';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { paths } from '@/app/paths';
import { Progress } from '@/components/ui/progress';
import { waitForAuthenticatedSession } from '@/utils/auth/client-session';
import { useEventTracking } from '@/hooks/useEventTracking';
import { EventType } from '@/api/types/events';

const MAX_FILE_SIZE_MB = 10;
/**
 * How long the progress bar takes to fill.
 *
 * Deliberately longer than the upload itself usually takes. The bar cannot
 * report real bytes -- fetch gives us no upload progress event here -- so the
 * honest thing to animate is elapsed time. Five seconds reads as work being
 * done; the old lookup table sat at a flat 78% and then jumped, which reads as
 * a picture of a progress bar rather than a progress bar.
 */
const UPLOAD_PROGRESS_MS = 5000;
type UploadStep = 'file' | 'auth';

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

export interface GetStartedFlowProps {
  /** Reported with the LOGIN event so we can tell the page from the dialog. */
  surface?: string;
  /** Fired once the session is confirmed, before the dashboard redirect. */
  onAuthenticated?: () => void;
}

/**
 * Upload a resume, then sign in — the two steps that turn a visitor into an
 * account, extracted from /get-started so the landing dialog runs the very
 * same code. Two copies of a conversion flow drift, and the half that drifts
 * is always the one nobody is watching.
 */
export function GetStartedFlow({ surface = 'upload_page', onAuthenticated }: GetStartedFlowProps) {
  const router = useRouter();
  const { status, update } = useSession();
  const { trackEvent } = useEventTracking({
    autoTrackPageViews: false,
    autoTrackScrollDepth: false,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loginEventTrackedRef = useRef(false);

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('No file chosen');
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const uploadStartedAtRef = useRef<number | null>(null);
  const [signInCompleted, setSignInCompleted] = useState(false);
  const [authCompletedThisVisit, setAuthCompletedThisVisit] = useState(false);
  const [isAuthSyncing, setIsAuthSyncing] = useState(false);
  const [authSyncProgress, setAuthSyncProgress] = useState(0);
  const [authSyncError, setAuthSyncError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState<UploadStep>('file');
  const [isDragActive, setIsDragActive] = useState(false);

  const isAuthenticated = useMemo(
    () => status === 'authenticated' || signInCompleted,
    [status, signInCompleted]
  );

  // Shared by both selection paths (file input change and drag-and-drop) so
  // there is exactly one place that turns a picked File into upload state.
  const applySelectedFile = (file: File) => {
    setUploadedFile(file);
    setFileName(file.name);
    setUploadStatus('idle');
    setUploadError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files[0]) return;
    applySelectedFile(files[0]);
  };

  // Drag-and-drop onto the upload zone. `dragOver` must keep calling
  // preventDefault on every event it receives -- without that, the browser
  // never allows `drop` to fire -- but only `dragEnter`/`dragLeave` toggle the
  // highlight, so hovering a drag over the zone doesn't re-render on every
  // pointer-move.
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // The zone has a button and text inside it, so the browser also fires
    // `dragleave` when the pointer crosses onto those children. Only clear
    // the highlight once the pointer has actually left the outer zone.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    applySelectedFile(file);
  };

  useEffect(() => {
    if (status === 'authenticated') {
      setSignInCompleted(true);
      setIsAuthSyncing(false);
      setAuthSyncProgress(100);
      setAuthSyncError(null);
    }
  }, [status]);

  useEffect(() => {
    if (!authCompletedThisVisit || loginEventTrackedRef.current) return;
    if (status !== 'authenticated' && !signInCompleted) return;

    loginEventTrackedRef.current = true;
    void trackEvent({
      event_type: EventType.LOGIN,
      element_id: 'upload-auth-complete',
      event_details: {
        auth_mode: 'sign-in',
        surface,
      },
    });
  }, [authCompletedThisVisit, signInCompleted, status, trackEvent]);

  useEffect(() => {
    const handleAuthSuccess = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data !== 'auth-success') return;

      void (async () => {
        setIsAuthSyncing(true);
        setAuthSyncError(null);
        setAuthSyncProgress(10);

        const synced = await waitForAuthenticatedSession(
          typeof update === 'function' ? update : undefined,
          {
            maxAttempts: 12,
            intervalMs: 250,
            onAttempt: (attempt, maxAttempts) => {
              const ratio = attempt / maxAttempts;
              setAuthSyncProgress(Math.min(96, Math.round(10 + ratio * 80)));
            },
          }
        );

        if (!synced) {
          setIsAuthSyncing(false);
          setAuthSyncProgress(0);
          setAuthSyncError("We couldn't verify your session yet. Please try signing in again.");
          return;
        }

        setAuthSyncProgress(100);
        setIsAuthSyncing(false);
        setSignInCompleted(true);
        setAuthCompletedThisVisit(true);
      })();
    };

    window.addEventListener('message', handleAuthSuccess);
    return () => window.removeEventListener('message', handleAuthSuccess);
  }, [update]);

  useEffect(() => {
    if (!uploadedFile || !isAuthenticated) return;
    if (uploadStatus === 'uploading' || uploadStatus === 'success') return;

    const uploadResume = async () => {
      setUploadStatus('uploading');
      setUploadError(null);

      try {
        const formData = new FormData();
        formData.append('file', uploadedFile);
        formData.append('title', uploadedFile.name);

        const response = await fetch('/api/settings/resumes', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw new Error(data?.error || 'Failed to upload resume. Please try again.');
        }

        setUploadStatus('success');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to upload resume. Please try again.';
        setUploadStatus('error');
        setUploadError(message);
      }
    };

    void uploadResume();
  }, [uploadedFile, isAuthenticated, uploadStatus]);

  // Ease toward 94% over UPLOAD_PROGRESS_MS and stop there. 100% is reserved
  // for an upload that has actually succeeded, so the bar never claims to be
  // finished before the server has said so. Driving this from elapsed time
  // rather than from uploadStatus matters: the server usually answers in a few
  // hundred milliseconds, so a status-driven bar has nothing to animate.
  useEffect(() => {
    if (uploadStatus !== 'uploading' && uploadStatus !== 'success') return;
    if (uploadStartedAtRef.current === null) uploadStartedAtRef.current = performance.now();

    let frame = 0;
    const tick = () => {
      const started = uploadStartedAtRef.current ?? performance.now();
      const p = Math.min(1, (performance.now() - started) / UPLOAD_PROGRESS_MS);
      // Decelerating, so it moves confidently at first and settles at the end.
      const eased = 1 - Math.pow(1 - p, 3);
      const ceiling = uploadStatus === 'success' ? 100 : 94;
      setUploadProgress(Math.round(4 + eased * (ceiling - 4)));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [uploadStatus]);

  // Animation frames stop in a background tab and are absent in some
  // environments entirely. Signing up must never depend on one firing, so
  // completion also lands on a timer: whatever happens to rAF, the upload can
  // still finish and the reader still gets their dashboard.
  useEffect(() => {
    if (uploadStatus !== 'success') return;
    const started = uploadStartedAtRef.current ?? performance.now();
    const remaining = Math.max(0, UPLOAD_PROGRESS_MS - (performance.now() - started));
    const timer = setTimeout(() => setUploadProgress(100), remaining);
    return () => clearTimeout(timer);
  }, [uploadStatus]);

  // Done means both things: the server accepted the file AND the bar has
  // arrived. Everything that says "finished" hangs off this, so nothing can
  // announce completion over a half-filled bar.
  const uploadComplete = uploadStatus === 'success' && uploadProgress >= 100;

  useEffect(() => {
    if (!uploadComplete || !isAuthenticated) return;
    const timer = setTimeout(() => {
      router.replace(paths.dashboard());
    }, 600);

    return () => clearTimeout(timer);
  }, [uploadComplete, isAuthenticated, router]);

  const hasSelectedFile = Boolean(uploadedFile);
  const canShowSignIn = hasSelectedFile && !isAuthenticated;

  useEffect(() => {
    if (hasSelectedFile) {
      setActiveStep('auth');
      return;
    }
    setActiveStep('file');
  }, [hasSelectedFile]);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  // Clears the current selection so the zone returns to its empty state. The
  // upload effect is keyed off `uploadedFile`, so this alone is enough to stop
  // any in-flight upload from mattering once it resolves.
  const handleRemoveFile = () => {
    setUploadedFile(null);
    setFileName('No file chosen');
    setUploadStatus('idle');
    setUploadError(null);
    setUploadProgress(0);
    uploadStartedAtRef.current = null;
  };

  // Re-runs the upload effect for the same file. Resetting status to 'idle'
  // (with the progress clock zeroed) is enough to trigger another attempt --
  // the effect fires on every `uploadStatus` change and only skips while it is
  // already 'uploading' or 'success'.
  const handleRetryUpload = () => {
    setUploadStatus('idle');
    setUploadError(null);
    setUploadProgress(0);
    uploadStartedAtRef.current = null;
  };

  const stepTwoStatus = isAuthenticated
    ? 'Signed in'
    : 'Sign in to continue';

  // On error the bar stops where it got to. Filling it to 100% and colouring it
  // red says the transfer completed and then failed, which is not what happened.
  const uploadProgressValue = uploadProgress;

  const uploadProgressLabel = uploadStatus === 'error'
    ? 'Upload failed'
    : uploadComplete
      ? 'Upload complete'
      : uploadStatus === 'uploading' || uploadStatus === 'success'
        ? 'Uploading resume'
        : 'Preparing upload';

  // The zone renders one of five mutually exclusive states. Order matters:
  // error and "fully complete" are checked before the more general
  // "uploading" bucket, since uploadStatus stays 'success' for a moment while
  // the progress bar is still easing toward 100.
  const fileSizeLabel = uploadedFile ? formatFileSize(uploadedFile.size) : null;
  const dropZoneContent = uploadStatus === 'error' ? (
    <>
      <div className="flex items-center gap-2 text-destructive">
        <ExclamationCircleIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
        <span className="text-sm font-semibold">{uploadError || 'Upload failed'}</span>
      </div>
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto p-0 text-sm"
        onClick={handleRetryUpload}
      >
        Try again
      </Button>
    </>
  ) : uploadComplete ? (
    <>
      <div className="flex items-center gap-2 text-emerald-600">
        <CheckCircleIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
        <span className="text-sm font-semibold">Upload complete</span>
      </div>
      <div className="flex w-full max-w-xs min-w-0 items-center justify-center gap-2 text-sm">
        <span className="min-w-0 truncate font-medium text-foreground">{fileName}</span>
        {fileSizeLabel ? <span className="shrink-0 text-muted-foreground">{fileSizeLabel}</span> : null}
      </div>
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto p-0 text-sm"
        onClick={openFilePicker}
      >
        Replace
      </Button>
    </>
  ) : uploadStatus === 'uploading' || uploadStatus === 'success' ? (
    <div className="flex w-full max-w-xs flex-col items-center gap-3">
      <div className="flex w-full min-w-0 items-center justify-center gap-2.5 text-sm">
        <DocumentTextIcon className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 truncate font-medium text-foreground">{fileName}</span>
      </div>
      <div className="w-full space-y-1.5">
        <Progress value={uploadProgressValue} className="h-1.5" />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{uploadProgressLabel}</span>
          <span>{uploadProgressValue}%</span>
        </div>
      </div>
    </div>
  ) : hasSelectedFile ? (
    <>
      <div className="flex w-full max-w-xs min-w-0 items-center justify-center gap-2.5 text-sm">
        <DocumentTextIcon className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 truncate font-medium text-foreground">{fileName}</span>
        {fileSizeLabel ? <span className="shrink-0 text-muted-foreground">{fileSizeLabel}</span> : null}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-sm"
          onClick={openFilePicker}
        >
          Replace
        </Button>
        <span className="text-muted-foreground" aria-hidden="true">·</span>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-sm"
          onClick={handleRemoveFile}
        >
          Remove
        </Button>
      </div>
    </>
  ) : (
    <>
      <CloudArrowUpIcon className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm font-medium text-foreground">Drag and drop your resume</p>
      <p className="text-sm text-muted-foreground">
        or{' '}
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-sm"
          onClick={(e) => {
            e.stopPropagation();
            openFilePicker();
          }}
        >
          choose file
        </Button>
      </p>
      <p className="text-xs text-muted-foreground">
        PDF, DOCX, DOC, or TXT · Max {MAX_FILE_SIZE_MB} MB
      </p>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Private to your account</span>
      </div>
    </>
  );

  return (
          <motion.div
            // Transform only, never opacity: this card is the conversion path,
            // and an entrance that gates visibility means any frame the
            // animation does not run is an empty modal. It must be readable
            // before framer-motion touches it.
            initial={{ y: 16 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={`bg-background max-sm:border-0 sm:border sm:border-border p-0 ${
              uploadStatus === 'error'
                ? 'sm:border-destructive/40'
                : ''
            }`}
            style={{ boxShadow: 'none' }}
          >
            <div className="grid grid-cols-2 border-b border-border bg-card">
              <button
                type="button"
                onClick={() => setActiveStep('file')}
                className={`px-4 py-3 text-left transition-colors border-r border-border ${
                  activeStep === 'file'
                    ? 'text-foreground'
                    : 'bg-card text-muted-foreground hover:bg-muted/30'
                }`}
                style={activeStep === 'file' ? { backgroundColor: 'var(--accent-soft)' } : undefined}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.16em] font-bold text-primary">Step 1</div>
                <div className="mt-1 text-sm font-semibold">Upload resume</div>
              </button>
              <button
                type="button"
                disabled={!hasSelectedFile}
                onClick={() => setActiveStep('auth')}
                className={`px-4 py-3 text-left transition-colors ${
                  activeStep === 'auth'
                    ? 'text-foreground'
                    : hasSelectedFile
                      ? 'bg-card text-muted-foreground hover:bg-muted/30'
                      : 'bg-card text-muted-foreground/70 cursor-not-allowed'
                }`}
                style={activeStep === 'auth' ? { backgroundColor: 'var(--accent-soft)' } : undefined}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.16em] font-bold text-primary">Step 2</div>
                <div className="mt-1 text-sm font-semibold">Sign in</div>
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              id="resume"
              name="resume"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileChange}
              className="hidden"
            />

            {activeStep === 'file' ? (
              <div className="p-5 lg:p-6">
                <div className="flex flex-col gap-5">
                  <p className="text-sm font-medium text-muted-foreground">
                    We’ll use it to personalize job matches and outreach. You can replace or remove it anytime.
                  </p>

                  <div
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={hasSelectedFile ? undefined : openFilePicker}
                    role={hasSelectedFile ? undefined : 'button'}
                    tabIndex={hasSelectedFile ? undefined : 0}
                    onKeyDown={hasSelectedFile ? undefined : (e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      e.preventDefault();
                      openFilePicker();
                    }}
                    className={`flex min-h-[180px] cursor-pointer flex-col items-center justify-center gap-3 border bg-card p-6 text-center transition-colors ${
                      isDragActive ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                  >
                    {dropZoneContent}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-5 lg:p-6">
                <div className="flex flex-col gap-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">Sign in to continue</h2>
                      <p className="text-sm text-muted-foreground mt-1.5">
                        Sign in with Google or email. You stay on this page.
                      </p>
                    </div>
                    {isAuthenticated ? (
                      <div className="flex items-center gap-2 text-emerald-600 font-semibold">
                        <CheckCircleIcon className="h-7 w-7" />
                      </div>
                    ) : null}
                  </div>

                  <div className="flex flex-col gap-3 border border-border/60 bg-muted/20 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">
                        Selected resume
                      </div>
                      <div className="text-sm font-semibold text-foreground truncate mt-1">{fileName}</div>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => setActiveStep('file')}>
                      Change file
                    </Button>
                  </div>

                  <div className={`text-sm font-medium ${isAuthenticated ? 'text-emerald-700' : 'text-foreground'}`}>
                    {stepTwoStatus}
                  </div>

                  {isAuthenticated ? (
                    <div className="flex items-center justify-between gap-4 bg-emerald-500/10 border border-emerald-500/20 px-4 py-4">
                      <div className="min-w-0 flex-1">
                        <div className="text-emerald-700 font-semibold">{stepTwoStatus}</div>
                        <div className="text-sm text-emerald-700/80">
                          We will finish the upload and send you to your dashboard.
                        </div>
                        <div className="mt-4 space-y-2">
                          <div className="flex items-center justify-between text-xs uppercase tracking-wide font-semibold">
                            <span className={uploadStatus === 'error' ? 'text-destructive' : 'text-emerald-700/90'}>
                              {uploadProgressLabel}
                            </span>
                            <span className={uploadStatus === 'error' ? 'text-destructive' : 'text-emerald-700/90'}>
                              {uploadProgressValue}%
                            </span>
                          </div>
                          <div className="h-2 overflow-hidden border border-emerald-500/20 bg-background/70">
                            <div
                              style={{ width: `${uploadProgressValue}%` }}
                              className={`relative h-full ${
                                uploadStatus === 'error'
                                  ? 'bg-destructive/70'
                                  : 'bg-emerald-600/80'
                              }`}
                            >
                              {!uploadComplete && uploadStatus !== 'error' && (
                                <motion.div
                                  aria-hidden="true"
                                  className="absolute inset-0 bg-[linear-gradient(110deg,transparent_20%,rgba(255,255,255,0.5)_45%,transparent_70%)]"
                                  animate={{ x: ['-120%', '140%'] }}
                                  transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Only once the upload is actually done. An enabled
                          "Go to dashboard" beside a bar still filling invites a
                          click that would leave mid-upload. */}
                      {uploadComplete ? (
                        <Button
                          type="button"
                          size="lg"
                          className="px-6"
                          onClick={() => router.replace(paths.dashboard())}
                        >
                          Go to dashboard
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] lg:items-start">
                      {canShowSignIn && (
                        <div className="upload-auth-shell border border-border bg-background px-5 py-5">
                          <UserAuthForm
                            mode="sign-in"
                            className="upload-auth-form w-full gap-4 [&_form]:w-full [&_input]:h-12 [&_input]:px-4 [&_input]:bg-background [&_button]:w-full [&_button]:h-12 [&_button]:text-[0.95rem] [&_button]:tracking-normal [&_.auth-divider-label]:bg-background [&_.auth-divider-label]:px-3"
                          />
                          {isAuthSyncing && (
                            <div className="mt-4 space-y-2 border border-primary/20 px-3 py-3" style={{ backgroundColor: "var(--accent-soft)" }}>
                              <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                                Finalizing sign in...
                              </p>
                              <Progress value={authSyncProgress} className="h-2" />
                            </div>
                          )}
                          {authSyncError && (
                            <p className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                              {authSyncError}
                            </p>
                          )}
                        </div>
                      )}

                      <div className="border border-border/60 bg-muted/25 px-4 py-4 space-y-3">
                        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-primary">
                          What happens next
                        </p>
                        <div className="space-y-2.5 text-sm text-muted-foreground">
                          {[
                            'Authenticate with email or Google in a secure tab.',
                            'We attach your selected resume to your account.',
                            'You’re redirected to your dashboard automatically.',
                          ].map((line, i) => (
                            <div key={i} className="flex items-start gap-2.5">
                              <div className="h-5 w-5 border border-border text-primary text-[11px] font-bold tabular-nums flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--accent-soft)" }}>
                                {i + 1}
                              </div>
                              <p className="leading-snug">{line}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
  );
}

